import React, { createContext, useContext, useState, useCallback } from 'react';
import ToastManager from '../components/Toast';

const ToastContext = createContext(null);

// Hook personalizzato per accedere al contesto
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast deve essere utilizzato all\'interno di un ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  // Generatore di ID univoci
  const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Funzione per aggiungere un nuovo toast
  const addToast = useCallback((message, options = {}) => {
    const id = generateId();
    const toast = {
      id,
      message,
      type: options.type || 'info',
      autoClose: options.autoClose === undefined ? 5000 : options.autoClose,
    };
    
    setToasts(prev => [...prev, toast]);
    return id;
  }, []);

  // Funzioni specifiche per i diversi tipi di toast
  const showError = useCallback((message, options = {}) => {
    return addToast(message, { ...options, type: 'error' });
  }, [addToast]);

  const showSuccess = useCallback((message, options = {}) => {
    return addToast(message, { ...options, type: 'success' });
  }, [addToast]);

  const showWarning = useCallback((message, options = {}) => {
    return addToast(message, { ...options, type: 'warning' });
  }, [addToast]);

  const showInfo = useCallback((message, options = {}) => {
    return addToast(message, { ...options, type: 'info' });
  }, [addToast]);

  // Rimuovere un toast specifico
  const removeToast = useCallback(id => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const contextValue = {
    addToast,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    removeToast
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastManager toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

export default ToastContext; 